/* ============================================================
   BED BUILDER — IN-STORE VIEWER (offline / kiosk)
   ─────────────────────────────────────────────────────────────
   Visualization only. No size, fabric, price, discount, or cart.
   Reuses images / swatches / stock data from bed-builder-config.js
   — make sure that file loads BEFORE this one on the page.
   ============================================================ */

const BBV_CONFIG = {
  // Public kiosk: reset the whole demo after this many ms of no interaction
  IDLE_RESET_MS: 90000, // 90 seconds

  // Paste your Google Apps Script Web App URL here (see setup instructions).
  // Leave blank to disable saving — the Save button will just show an error.
  SAVE_ENDPOINT_URL: 'https://script.google.com/macros/s/AKfycbzSzi4IhOQNp2Rdq9Ts-zc0bm-YnX4jtjKz0MtH_kN6Fv_I5cK90mKfFQXbShNlpa8V/exec',

  // No code is shown on screen — the RM15 (min. spend RM200) voucher is
  // issued manually to each lead's email after the event.
}

// Only these colours show PREORDER when out of stock — everyone else shows SOLD OUT
const BBV_PREORDER_ALLOWED_COLOURS = ['pyjama_stripes_oak', 'sleepover_stripes_blue', 'canvas_beige']

const BBV_PRODUCTS = [
  { id: 'fitted_sheet',     title: 'Fitted Sheet',     isAddon: false },
  { id: 'pillowcase_pair',  title: 'Pillowcase Pair',  isAddon: false },
  { id: 'duvet_cover',      title: 'Duvet Cover',      isAddon: false },
  { id: 'bolstercase',      title: '+Bolstercase',     isAddon: true  },
  { id: 'pillow_case',      title: '+Pillow Case',     isAddon: true  },
]

// Pull the colour list straight from the shared colour definitions used by
// the main builder's config (image URLs), so both pages always match.
function bbvGetColours(productId) {
  const images = window.BB_IMAGES?.[productId] || {}
  return Object.keys(images).filter(id => images[id]) // only colours with an actual image
}

const bbvState = {
  size: null, // global size — drives stock/preorder checks below
  selections: {}, // { item_id: { colorId, layerImage } }
  pending: {},
  expandedItem: null,
  idleTimer: null,
}

document.addEventListener('DOMContentLoaded', () => {
  buildSizeRow()
  loadImagesFromConfig()
  buildItems()
  initZoom()
  initReset()
  initSave()
  initIdleWatcher()
})

/* ============================================================
   SIZE SELECTOR — reuses window.BB_SIZES from bed-builder-config.js
   ============================================================ */
function buildSizeRow() {
  const row   = document.getElementById('bbv-size-row')
  const sizes = window.BB_SIZES || {}
  const keys  = Object.keys(sizes)
  if (!row || keys.length === 0) return

  bbvState.size = keys[0] // default to the first configured size

  row.innerHTML = keys.map(key => `
    <button class="bbv-size-pill${key === bbvState.size ? ' active' : ''}"
      type="button" data-size="${key}">${sizes[key].label}</button>
  `).join('')

  row.querySelectorAll('.bbv-size-pill').forEach(btn => {
    btn.addEventListener('click', () => onSizeChange(btn.dataset.size))
  })
}

function onSizeChange(newSize) {
  bbvState.size = newSize

  document.querySelectorAll('.bbv-size-pill').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.size === newSize)
  })

  // Stock/preorder status can change per size — refresh everything
  BBV_PRODUCTS.forEach(product => {
    buildSwatches(product) // rebuilds swatches with updated sold-out state

    const sel = bbvState.selections[product.id] || bbvState.pending[product.id]
    if (sel) {
      highlightSwatch(product.id, sel.colorId) // re-apply since swatches were just rebuilt
      renderShipInfo(product.id, sel.colorId)
    }
    if (bbvState.selections[product.id]) {
      updateRowUI(product.id, true)
    }
  })
}

/* ============================================================
   IMAGES
   ============================================================ */
function loadImagesFromConfig() {
  const imgs = window.BB_IMAGES || {}
  const base = document.getElementById('bbv-layer-base')
  if (base && imgs.base) base.src = imgs.base

  const defs = imgs.defaults || {}
  ;['pillowcase_pair', 'duvet_cover', 'bolstercase', 'pillow_case'].forEach(id => {
    const el = document.getElementById(`bbv-layer-${id}-default`)
    if (el && defs[id]) el.src = defs[id]
  })
}

/* ============================================================
   AVAILABILITY — derived from BB_PRICING, checked against the
   currently selected bbvState.size (ignoring price itself)
   ============================================================ */
function bbvGetSizeEntry(productId, colorId) {
  const fallback = { available: true, shipDate: '' }
  if (!bbvState.size) return fallback
  return window.BB_PRICING?.[productId]?.[colorId]?.[bbvState.size] || fallback
}

function bbvIsColourAvailable(productId, colorId) {
  return bbvGetSizeEntry(productId, colorId).available
}

function bbvGetShipDate(productId, colorId) {
  return bbvGetSizeEntry(productId, colorId).shipDate || ''
}

// 'instock' | 'preorder' | 'soldout'
function bbvGetStatus(productId, colorId) {
  if (bbvIsColourAvailable(productId, colorId)) return 'instock'
  return BBV_PREORDER_ALLOWED_COLOURS.includes(colorId) ? 'preorder' : 'soldout'
}

/* ============================================================
   BUILD ITEM ROWS
   ============================================================ */
function buildItems() {
  const container = document.getElementById('bbv-items')
  container.innerHTML = ''

  BBV_PRODUCTS.forEach(product => {
    const itemEl = document.createElement('div')
    itemEl.className = 'bbv-item'
    itemEl.dataset.itemId = product.id

    itemEl.innerHTML = `
      <div class="bbv-item__header" role="button" tabindex="0" aria-expanded="false">
        <span class="bbv-item__dot" id="bbv-dot-${product.id}"></span>
        <span class="bbv-item__name">${product.title}</span>
        <span class="bbv-item__status" id="bbv-status-${product.id}"></span>
        <span class="bbv-item__chevron" aria-hidden="true">&#8964;</span>
        <span class="bbv-item__check" id="bbv-check-${product.id}" aria-hidden="true">
          <svg viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="1,4 3.5,6.5 9,1" stroke="white" stroke-width="2.2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </div>
      <div class="bbv-item__panel" id="bbv-panel-${product.id}" role="region" aria-hidden="true">
        <div class="bbv-swatch-label">Choose Colour</div>
        <div class="bbv-swatches" id="bbv-swatches-${product.id}"></div>
        <div class="bbv-shipinfo" id="bbv-shipinfo-${product.id}"></div>
        <button class="bbv-confirm-btn" type="button" data-confirm="${product.id}">SELECT</button>
        <button class="bbv-clear-btn" type="button" data-remove="${product.id}">Clear Selection</button>
      </div>
    `

    container.appendChild(itemEl)
    buildSwatches(product)

    itemEl.querySelector('.bbv-item__header').addEventListener('click', () => toggleItem(product.id))
  })

  container.addEventListener('click', e => {
    const confirmBtn = e.target.closest('[data-confirm]')
    const removeBtn  = e.target.closest('[data-remove]')
    if (confirmBtn) confirmItem(confirmBtn.dataset.confirm)
    if (removeBtn)  removeItem(removeBtn.dataset.remove)
  })
}

function buildSwatches(product) {
  const wrap = document.getElementById(`bbv-swatches-${product.id}`)
  if (!wrap) return
  wrap.innerHTML = ''

  bbvGetColours(product.id).forEach(colorId => {
    const swatchImg = window.BB_SWATCH_IMAGES?.[colorId]
    const status    = bbvGetStatus(product.id, colorId)
    const isSoldOut = status === 'soldout'

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'bbv-swatch' + (isSoldOut ? ' sold-out' : '')
    btn.dataset.colorId = colorId
    btn.disabled = isSoldOut
    btn.title = isSoldOut ? 'Sold Out' : ''

    if (swatchImg) {
      const img = document.createElement('img')
      img.src = swatchImg
      img.alt = colorId
      btn.appendChild(img)
    }

    btn.addEventListener('click', () => {
      if (isSoldOut) return
      selectColor(product.id, colorId)
    })

    wrap.appendChild(btn)
  })
}

/* ============================================================
   FOCUS / EXPAND
   ============================================================ */
function toggleItem(itemId) {
  const isExpanding = bbvState.expandedItem !== itemId
  collapseAll()
  if (isExpanding) expandItem(itemId)
}

function expandItem(itemId) {
  bbvState.expandedItem = itemId
  const itemEl = document.querySelector(`[data-item-id="${itemId}"]`)
  itemEl.classList.add('expanded')
  itemEl.querySelector('.bbv-item__header').setAttribute('aria-expanded', 'true')
  itemEl.querySelector('.bbv-item__panel').setAttribute('aria-hidden', 'false')

  document.querySelectorAll('.bbv-item').forEach(el => {
    if (el.dataset.itemId !== itemId) el.classList.add('focus-hidden')
  })

  const sel = bbvState.pending[itemId] || bbvState.selections[itemId]
  if (sel) highlightSwatch(itemId, sel.colorId)

  itemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function collapseAll() {
  if (bbvState.expandedItem) {
    const itemEl = document.querySelector(`[data-item-id="${bbvState.expandedItem}"]`)
    if (itemEl) {
      itemEl.classList.remove('expanded')
      itemEl.querySelector('.bbv-item__header').setAttribute('aria-expanded', 'false')
      itemEl.querySelector('.bbv-item__panel').setAttribute('aria-hidden', 'true')
    }
  }
  document.querySelectorAll('.bbv-item').forEach(el => el.classList.remove('focus-hidden'))
  bbvState.expandedItem = null
}

/* ============================================================
   COLOUR SELECTION
   ============================================================ */
function selectColor(itemId, colorId) {
  const layerImage = window.BB_IMAGES?.[itemId]?.[colorId] || ''
  bbvState.pending[itemId] = { colorId, layerImage }

  highlightSwatch(itemId, colorId)
  showLayer(itemId, layerImage)
  renderShipInfo(itemId, colorId)
}

function highlightSwatch(itemId, colorId) {
  document.querySelectorAll(`#bbv-swatches-${itemId} .bbv-swatch`).forEach(s => {
    s.classList.toggle('selected', s.dataset.colorId === colorId)
  })
}

function renderShipInfo(itemId, colorId) {
  const el = document.getElementById(`bbv-shipinfo-${itemId}`)
  if (!el) return
  const status = bbvGetStatus(itemId, colorId)
  el.textContent = status === 'preorder' ? bbvGetShipDate(itemId, colorId) : ''
}

/* ============================================================
   CONFIRM / REMOVE
   ============================================================ */
function confirmItem(itemId) {
  const pending = bbvState.pending[itemId]
  if (!pending) { collapseAll(); return }

  bbvState.selections[itemId] = pending
  delete bbvState.pending[itemId]

  updateRowUI(itemId, true)
  showLayer(itemId, pending.layerImage)
  updateGreyDefaultsVisibility()
  collapseAll()
}

function removeItem(itemId) {
  delete bbvState.selections[itemId]
  delete bbvState.pending[itemId]

  updateRowUI(itemId, false)
  hideLayer(itemId)
  updateGreyDefaultsVisibility()
  collapseAll()
}

function updateRowUI(itemId, isSelected) {
  const sel    = bbvState.selections[itemId]
  const dot    = document.getElementById(`bbv-dot-${itemId}`)
  const check  = document.getElementById(`bbv-check-${itemId}`)
  const statusEl = document.getElementById(`bbv-status-${itemId}`)

  dot?.classList.toggle('active', isSelected)
  check?.classList.toggle('checked', isSelected)

  if (statusEl) {
    if (isSelected && sel) {
      const status = bbvGetStatus(itemId, sel.colorId)
      if (status === 'preorder') statusEl.innerHTML = `<span class="bbv-preorder-badge">PREORDER</span>`
      else if (status === 'soldout') statusEl.innerHTML = `<span class="bbv-soldout-badge">SOLD OUT</span>`
      else statusEl.innerHTML = ''
    } else {
      statusEl.innerHTML = ''
    }
  }
}

/* ============================================================
   IMAGE LAYERS
   ============================================================ */
function showLayer(itemId, src) {
  const layerEl = document.getElementById(`bbv-layer-${itemId}`)
  if (!layerEl) return
  if (src) layerEl.src = src
  layerEl.classList.add('visible')
}

function hideLayer(itemId) {
  document.getElementById(`bbv-layer-${itemId}`)?.classList.remove('visible')
}

const BBV_GREY_DEFAULT_LAYER_IDS = [
  'base',
  'pillowcase_pair-default',
  'duvet_cover-default',
  'bolstercase-default',
  'pillow_case-default',
]

function getSelectedCount() {
  return BBV_PRODUCTS.filter(p => bbvState.selections[p.id]).length
}

function updateGreyDefaultsVisibility() {
  const anySelected = getSelectedCount() > 0
  BBV_GREY_DEFAULT_LAYER_IDS.forEach(layerId => {
    document.getElementById(`bbv-layer-${layerId}`)?.classList.toggle('visible', !anySelected)
  })
}

/* ============================================================
   RESET / START OVER
   ============================================================ */
function initReset() {
  document.getElementById('bbv-reset-btn')?.addEventListener('click', resetAll)
}

function resetAll() {
  bbvState.selections = {}
  bbvState.pending = {}
  collapseAll()

  BBV_PRODUCTS.forEach(p => {
    updateRowUI(p.id, false)
    hideLayer(p.id)
    highlightSwatch(p.id, null)
    const shipEl = document.getElementById(`bbv-shipinfo-${p.id}`)
    if (shipEl) shipEl.textContent = ''
  })

  updateGreyDefaultsVisibility()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/* ============================================================
   SAVE LOOK — logs the look, then opens the lead-capture form
   ============================================================ */
function initSave() {
  document.getElementById('bbv-save-btn')?.addEventListener('click', saveLook)
  initLeadModal()
}

// Builds the size + per-item-colour fields shared by both the "look"
// log and the "lead" log, so both rows can be cross-referenced if needed.
function buildLookFields() {
  const fields = { size: bbvState.size || '' }
  BBV_PRODUCTS.forEach(p => {
    fields[p.id] = bbvState.selections[p.id]?.colorId || ''
  })
  return fields
}

async function saveLook() {
  if (getSelectedCount() === 0) {
    showSaveToast('Select at least one item first')
    return
  }

  if (!BBV_CONFIG.SAVE_ENDPOINT_URL) {
    showSaveToast('Save not set up yet — see setup guide')
    return
  }

  const btn = document.getElementById('bbv-save-btn')
  btn.disabled = true
  btn.textContent = 'Saving...'

  const payload = {
    type: 'look',
    timestamp: new Date().toISOString(),
    ...buildLookFields(),
  }

  try {
    // no-cors: Google Apps Script doesn't return CORS headers by default.
    // We can't read the response, but the row still gets appended server-side.
    await fetch(BBV_CONFIG.SAVE_ENDPOINT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
    })
    showSaveToast('Look saved!')
    openLeadModal()
  } catch (err) {
    console.error('[BedBuilderViewer] Save failed:', err)
    showSaveToast('Could not save — check your connection')
  } finally {
    btn.disabled = false
    btn.textContent = 'Save Look'
  }
}

function showSaveToast(msg) {
  const toast = document.getElementById('bbv-save-toast')
  if (!toast) return
  toast.textContent = msg
  toast.classList.add('visible')
  setTimeout(() => toast.classList.remove('visible'), 2500)
}

/* ============================================================
   LEAD CAPTURE — Name / Email / Birthday → discount code
   ============================================================ */
function initLeadModal() {
  document.getElementById('bbv-lead-close')?.addEventListener('click', closeLeadModal)
  document.getElementById('bbv-lead-overlay')?.addEventListener('click', e => {
    if (e.target.id === 'bbv-lead-overlay') closeLeadModal()
  })
  document.getElementById('bbv-lead-done-btn')?.addEventListener('click', closeLeadModal)
  document.getElementById('bbv-lead-form')?.addEventListener('submit', submitLead)
}

function openLeadModal() {
  // Always start back on the form view in case it's reopened after a previous submit
  document.getElementById('bbv-lead-form-view').style.display = ''
  document.getElementById('bbv-lead-success-view').style.display = 'none'
  document.getElementById('bbv-lead-form')?.reset()
  document.getElementById('bbv-lead-overlay')?.classList.add('active')
}

function closeLeadModal() {
  document.getElementById('bbv-lead-overlay')?.classList.remove('active')
}

async function submitLead(e) {
  e.preventDefault()

  const name     = document.getElementById('bbv-lead-name').value.trim()
  const email    = document.getElementById('bbv-lead-email').value.trim()
  const phone    = document.getElementById('bbv-lead-phone').value.trim()
  const birthday = document.getElementById('bbv-lead-birthday').value

  if (!name || !email || !phone) return

  const submitBtn = e.target.querySelector('.bbv-lead-submit')
  submitBtn.disabled = true
  submitBtn.textContent = 'Submitting...'

  const payload = {
    type: 'lead',
    timestamp: new Date().toISOString(),
    name,
    email,
    phone,
    birthday,
    ...buildLookFields(),
  }

  try {
    if (BBV_CONFIG.SAVE_ENDPOINT_URL) {
      await fetch(BBV_CONFIG.SAVE_ENDPOINT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      })
    }
    showThankYou()
  } catch (err) {
    console.error('[BedBuilderViewer] Lead save failed:', err)
    showThankYou() // still confirm — don't block the customer over a logging error
  } finally {
    submitBtn.disabled = false
    submitBtn.textContent = 'Submit'
  }
}

function showThankYou() {
  document.getElementById('bbv-lead-form-view').style.display = 'none'
  document.getElementById('bbv-lead-success-view').style.display = ''
}

/* ============================================================
   IDLE AUTO-RESET (public kiosk)
   ============================================================ */
function initIdleWatcher() {
  const resetTimer = () => {
    clearTimeout(bbvState.idleTimer)
    bbvState.idleTimer = setTimeout(() => {
      if (getSelectedCount() > 0) {
        resetAll()
        showIdleToast()
      }
    }, BBV_CONFIG.IDLE_RESET_MS)
  }

  ;['click', 'touchstart', 'scroll', 'keydown'].forEach(evt =>
    document.addEventListener(evt, resetTimer, { passive: true }))

  resetTimer()
}

function showIdleToast() {
  const toast = document.getElementById('bbv-idle-toast')
  if (!toast) return
  toast.classList.add('visible')
  setTimeout(() => toast.classList.remove('visible'), 3000)
}

/* ============================================================
   ZOOM
   ============================================================ */
function initZoom() {
  const overlay   = document.getElementById('bbv-zoom-overlay')
  const closeBtn  = document.getElementById('bbv-zoom-close')
  const zoomBtn   = document.getElementById('bbv-btn-zoom')
  const composite = document.getElementById('bbv-zoom-composite')

  zoomBtn?.addEventListener('click', () => {
    if (composite) {
      composite.innerHTML = ''
      document.querySelectorAll('#bbv-image-wrap .bbv-layer').forEach(layer => {
        const clone = document.createElement('img')
        clone.className = 'bbv-layer' + (layer.classList.contains('visible') ? ' visible' : '')
        clone.src = layer.src || ''
        clone.alt = ''
        composite.appendChild(clone)
      })
    }
    overlay.classList.add('active')
  })

  const close = () => overlay.classList.remove('active')
  closeBtn?.addEventListener('click', close)
  overlay?.addEventListener('click', e => { if (e.target === overlay) close() })
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close() })
}